import { getEntity, JoiEvent, MediaTerms } from "globals/store/events.data"
import { Performer } from "globals/store/performers.data"
import { Program, EntityRequest, getTimeFromProgramSession, Session } from "globals/store/programs.data"
import { BriefType, ItemStatus, LogisticsShareable, PublicEditEntityAssetsInfo, PublicEditProfileInfo, PublicEditProfileStatusTypesType, PublishInfo, SessionItemInfo, ShareOSSInfo, shareUrlsByType, StandItemInfo } from "globals/store/share.data"
import { compactDate, flatten, getNestedSet, sortByProp, sortByPropOfNumber, str2Date, time_to_min, getList } from 'globals/helpers/helpers';
import { DAY_MS } from "globals/helpers/constants";
import { LogisticsInBriefSelection, PublishInBriefSelection } from "globals/store/publish.data";
import { ExhibitorStand } from "globals/store/exhibitors.data";
import { Asset } from "globals/store/assets.data";
import { Budget, PublicQuoteInfo } from "globals/store/reselectors/budget.data";
import { CoreEntityType } from 'globals/store/share.data';
import { EntityBase, EntityBaseWithDeprecations } from "globals/store/entity.shared.data";
;

// DON'T IMPORT NON-DATA FILES

export interface BriefAction {
  submitted?: boolean
  baseUrl: string
}

export interface BriefActionWithId extends BriefAction {
  id: string
}

export interface SessionBriefAction {
  title: string
  subTitle: string
  synopsis: BriefAction
  staging: BriefAction
  date: number
}

export interface SubSession {
  title: string
  subTitle: string
  date: number
}

export interface AssetBriefAction extends BriefAction {
  name: string
  icon?: string
  assetId: string
  status: ItemStatus
  description?: string
  sessions?:  SubSession[]
  stands?: SubSession[]
}



export interface StageBriefAction {
  title: string
  subTitle: string
  staging: BriefAction
  date: number
}

export interface QuoteBriefAction extends BriefAction {
  name: string
}

export interface BriefActions {
  profile?: BriefAction
  terms?: BriefActionWithId[]
  sessions?: SessionBriefAction[]
  stands?: StageBriefAction[]
  total?: number
  submitted?: number
  // assets?: AssetBriefAction[]
  // presentationAssets?: AssetBriefAction[]
  standAssets?: AssetBriefAction[]
  quotes?: QuoteBriefAction[]
  associatedAssets?: BriefAction
}

export const getBriefProgramPublishes = (publishInfos: { [id:string]: PublishInfo } = {}, publishIdsToShow: string[] = [], briefType: CoreEntityType, event: JoiEvent): PublishInBriefSelection[] => 
  briefType === 'performer' && !event.showPublishedProgramsInPerformerBrief ? null :
  briefType === 'exhibitor' && !event.showPublishedProgramsInExhibitorBrief ? null :
  briefType === 'resource' && !event.showPublishedProgramsInResourceBrief ? null :
  briefType === 'attendee' && !event.showPublishedProgramsInAttendeeBrief ? null :
  publishIdsToShow.map(id => publishInfos[id]).filter(info => info && info.status === 'published').map(({ publishCode, title }) => ({ publishCode, title }))

export const getBriefLogisticsViews = (logisticsShareables: { [id: string]: LogisticsShareable }, viewIdsToShow: string[] = [], briefType: CoreEntityType, event: JoiEvent): LogisticsInBriefSelection[] =>
  briefType === 'performer' && !event.showLogisticsInPBrief ? null :
  briefType === 'exhibitor' && !event.showLogisticsInEBrief ? null :
  briefType === 'resource' && !event.showLogisticsInRBrief ? null :
  briefType === 'attendee' && !event.showLogisticsInABrief ? null :
  viewIdsToShow.map(id => logisticsShareables[id]).filter(info => info && info.status === 'shared').map(({ code, title }) => ({ code, title }))

export const getBriefOnsiteViews = (onsiteShareables: ShareOSSInfo[], viewIdsToShow: string[] = [], briefType: CoreEntityType, event: JoiEvent): LogisticsInBriefSelection[] =>
  briefType === 'performer' && !event.showShareInPBrief ? null :
  briefType === 'exhibitor' && !event.showShareInEBrief ? null :
  briefType === 'resource' && !event.showShareInRBrief ? null :
  briefType === 'attendee' && !event.showShareInABrief ? null :
  onsiteShareables.filter(s => viewIdsToShow.includes(s.id)).filter(info => info && info.status === 'shared').map(({ id, title }) => ({ code: id, title }))

export const getValidSortedStagingRequests = (stagingRequests: EntityRequest[], performers: { [id:string]: Performer }): EntityRequest[] => {
  // console.log({ stagingRequests, performers })
  return (stagingRequests || [])
  .filter(s => !s.by_uid || performers[s.by_uid])
  .map(s => !s.by_uid ? s : { ...s, by: performers[s.by_uid].name })
  .sort(sortByProp('by'))
}


const mutateActionsCounts = (briefActions: BriefActions) => {
  const { sessions: s, stands: st, profile, terms, standAssets: sa, quotes: q, associatedAssets: aa } = briefActions
  briefActions.total = (profile ? 1 : 0) + (!aa ? 0 : 1) +
    (!terms ? 0 : terms.length) + 
    (!st ? 0 : st.length) +
    (!s ? 0 : s.reduce((t, { synopsis: sy, staging: stag }) =>  t + (sy ? 1 : 0) + (stag ? 1 : 0), 0)) +
    (!sa ? 0 : sa.length) +
    (!q ? 0 : q.length) 

  briefActions.submitted = (profile?.submitted ? 1 : 0) + (aa?.submitted ? 1 : 0) +
    (!terms ? 0 : terms.reduce((t, { submitted }) =>  t + (submitted ? 1 : 0), 0)) +
    (!st ? 0 : st.reduce((t, { staging }) =>  t + (staging?.submitted ? 1 : 0), 0)) +
    (!s ? 0 : s.reduce((t, { synopsis: sy, staging: stag }) =>  t + (sy?.submitted ? 1 : 0) + (stag?.submitted ? 1 : 0), 0)) + 
    (!sa ? 0 : sa.reduce((t, { submitted }) => t + (submitted ? 1 : 0), 0)) + 
    (!q ? 0 : q.reduce((t, { submitted }) => t + (submitted ? 1 : 0), 0))
}

export const entitiesByAssetIdsTransformer = <E, K extends keyof E, S extends keyof E>(entityList: E[], assetIdsKey: K, assets: { [id: string]: Asset }, sortKey?: S): { [id: string]: E[] } => {
  const entitiesByAssetId: { [id: string]: E[] } = {}
  Object.keys(assets).forEach(id => entitiesByAssetId[id] = [])

  entityList.filter(e => !!e[assetIdsKey as string]).forEach(e => e[assetIdsKey as string].filter(id => !!assets[id]).forEach(id => 
    entitiesByAssetId[id].push(e)
  ))
  if (sortKey) Object.keys(entitiesByAssetId).forEach(k => entitiesByAssetId[k].sort(sortByProp(sortKey)))
  return entitiesByAssetId
}

const programsToAssetSessionList = (programList: Program[]) => flatten(programList.map(p => Object.values(p.sessions_obj).map((s: Session) => {
  const date = getTimeFromProgramSession(p, s)
  const dateText = !p.date ? `Day ${s.on_day} ${s.start}-${s.end}` : 
    `${compactDate(new Date(date))} ${s.start}-${s.end}`
  s.assetSessionTitle = `${p.title || 'Unnamed Program'} - ${s.title || 'Unnamed Session'} - ${dateText}`
  s.assetDate = date
  s.assetSubTitle = `${time_to_min(s.end) - time_to_min(s.start)} minutes | ${s.start} - ${s.end}, ${ compactDate(new Date(date)) } | ${ p.title}`
  return s
})))

// const extractAssetStandList = (stands: ExhibitorStand[], programs: { [id: string]: Program })


export const getExhibitorStandIdsByExhibitorId = (exhibitorStandList: ExhibitorStand[], exhibitorId: string) => exhibitorStandList
    .filter(s => s.exhibitorId === exhibitorId)
    .sort(sortByPropOfNumber('exhibitorIndex'))
    .map(s => s.id)


const getMediaTermsBriefActions = <E extends EntityBase>(allTerms: MediaTerms[], entity: E): BriefActionWithId[] => 
  allTerms.filter(t => !t.typeIds?.length || t.typeIds.includes(entity.entityTypeId))
    .filter(t => !!t.use && !entity.noTermsMap[t.id])
    .map(t => ({ submitted: entity.mediaTermsMap[t.id] === 'Accepted', baseUrl: t.url, id: t.id }))

export const getExhibitorBriefActions = (profiles: { [id: string]: PublicEditProfileInfo }, event: JoiEvent, exhibitorStandList: ExhibitorStand[], programs: { [pId:string]: Program }, eRequest: { [id: string]: StandItemInfo }, associatedAssetInfos: { [id: string]: PublicEditEntityAssetsInfo },  exhibitorId: string): 
  BriefActions => {

  if (!event?.exhibitors) return null
  const exhibitor = event.exhibitors[exhibitorId]
  if (!exhibitor) return null
  const profileShareable = profiles[exhibitorId]

  const associatedAssetInfo = !associatedAssetInfos ? null : associatedAssetInfos[exhibitorId]
  const briefActions: BriefActions = {
    associatedAssets: !exhibitor.showAssetsLinkInBrief || !associatedAssetInfo || associatedAssetInfo.status === 'Off' ? null : {
      submitted: associatedAssetInfo.status === 'Submitted',
      baseUrl: `${shareUrlsByType.entityAsset}/${associatedAssetInfo.code}`
    },
    profile: !profileShareable || !exhibitor.showEditProfileLinkInBrief ? null : { submitted: profileShareable.status === 'Submitted', baseUrl: `${shareUrlsByType.exhibitor}/${profileShareable.code}` },
    terms: getMediaTermsBriefActions(event.mediaTerms.exhibitor, exhibitor),
    stands: [],
    standAssets: []
  }

  const standIds = getExhibitorStandIdsByExhibitorId(exhibitorStandList, exhibitorId)
  standIds
    .map(id => event.exhibitorStands[id])
    .filter(({ programId }) => !!programs[programId])
    .forEach(stand => {
      const program = programs[stand.programId]
      const typeName = event.standTypes.find(r => r.id === stand.standTypeId)?.name || ''
      const progName = program.title || ''
      const date = !program.date ? DAY_MS : str2Date(program.date).getTime()
      const combinedId = `${stand.id}_${exhibitorId}`
      const staging = eRequest[combinedId]
      briefActions.stands.push({ 
        title: stand.name,
        subTitle: `${progName}${progName && typeName ? ' | ' : ''}${typeName}`,
        staging: !staging || staging.status === 'Off' ? null : { submitted: staging.status === 'Submitted', baseUrl: `${shareUrlsByType.eRequest}/${staging.id}` },
        date
      })
  })
  briefActions.stands = briefActions.stands.filter(s => s.staging).sort((a,b) => a.date - b.date)


  if (event.assets) {
    const assetsToSubmit = Object.values(event.assets).filter((a: Asset) => a.submitterId === exhibitorId && a.status !== 'Off')


    const standsWithAssetsList: ExhibitorStand[] = Object.values(event.exhibitorStands).filter((s: ExhibitorStand) => s.assetIds && assetsToSubmit.some((a: Asset) => s.assetIds.includes(a.id)))

    const standsByAssetId = entitiesByAssetIdsTransformer(standsWithAssetsList, 'assetIds', event.assets, 'name')
    const allStandAssetIds = getNestedSet(standsWithAssetsList, 'assetIds')
    const assetIds = getList(assetsToSubmit, 'id')

    const standAssetIds = assetIds.filter(id => allStandAssetIds.has(id))
    const remainingAssetIds = assetIds.filter(id => !allStandAssetIds.has(id))

    standAssetIds.forEach(assetId => {
      const { name, description, icon, status, id } = event.assets[assetId]
      const stands: ExhibitorStand[] = standsByAssetId[assetId]


      briefActions.standAssets.push({
        name, description, icon, assetId: id, submitted: status === 'Submitted', status,
        baseUrl: `${shareUrlsByType.fullAsset}/${event.eventId}/${assetId}`,
        stands: stands.map(({ programId, name: standName }) => {
          const p = programs[programId]
          const date = p?.date ? str2Date(p.date).getTime() : 0
          return {
            title: standName || 'Unnamed exhibitor stand',
            date,
            subTitle: !p ? 'Not on a program' : 
              `${ date ? compactDate(new Date(date)) : 'Undated program' } | ${ p.title || 'Untitled program'}`
          }
        })
      })
    })

  }


  mutateActionsCounts(briefActions)
  return briefActions
}

export const getPerformerBriefActions = (profiles: { [id: string]: PublicEditProfileInfo }, synopses: { [id:string]: SessionItemInfo }, stagings: { [id:string]: SessionItemInfo }, assets: { [id:string]: SessionItemInfo }, event: JoiEvent, programs: Program[], associatedAssetInfos: { [id: string]: PublicEditEntityAssetsInfo }, performerId: string ): BriefActions => {

  if (!profiles || !synopses || !stagings || !assets || !event?.performers || !programs) {
    // console.log("Get brief actions missing data!")
    return null
  }

  const performer = event.performers[performerId] 
  if (!performer) return null
  const profileShareable = profiles[performerId]

  const associatedAssetInfo = !associatedAssetInfos ? null : associatedAssetInfos[performerId]
  const briefActions: BriefActions = {
    associatedAssets: !performer.showAssetsLinkInBrief || !associatedAssetInfo || associatedAssetInfo.status === 'Off' ? null : {
      submitted: associatedAssetInfo.status === 'Submitted',
      baseUrl: `${shareUrlsByType.entityAsset}/${associatedAssetInfo.code}`
    },
    profile: !profileShareable || !performer.showEditProfileLinkInPerformerBrief ? null : { submitted: profileShareable.status === 'Submitted', baseUrl: `${shareUrlsByType.profile}/${profileShareable.code}` },
    terms: getMediaTermsBriefActions(event.mediaTerms.performer, performer),
    sessions: [],
  }
  
  programs.forEach(program => Object.values(program.sessions_obj).filter((s: Session) => s.performerIds && s.performerIds.includes(performerId)).forEach((s: Session) => {
    const date = !program.date ? s.on_day * DAY_MS : str2Date(program.date).getTime() + (+s.on_day - 1) * DAY_MS + time_to_min(s.start) * 60000
    const sessionCombinedId = `${s.id}_${performerId}`
    const synopsis = synopses[s.id]
    const staging = stagings[sessionCombinedId]

    briefActions.sessions.push({ 
      title: s.title,
      subTitle: `${time_to_min(s.end) - time_to_min(s.start)} minutes | ${s.start} - ${s.end}, ${ compactDate(new Date(date)) } | ${ program.title}`,
      synopsis: !synopsis || synopsis.performerId !== performerId || synopsis.status === 'Off' ? null : { submitted: synopsis.status === 'Submitted', baseUrl: `${shareUrlsByType.synopsis}/${synopsis.id}` },
      staging: !staging || staging.status === 'Off' ? null : { submitted: staging.status === 'Submitted', baseUrl: `staging-request/${staging.id}` },
      date
    })
  }))
  briefActions.sessions = briefActions.sessions.filter(s => s.staging || s.synopsis).sort((a,b) => a.date - b.date)

  mutateActionsCounts(briefActions)

  return briefActions
}


export const getAttendeeBriefActions = (profiles: { [id: string]: PublicEditProfileInfo }, event: JoiEvent, attendeeId: string ): BriefActions => {

  if (!profiles || !event) {
    // console.log("Get brief actions missing data!")
    return null
  }

  const attendee = event.attendees[attendeeId]
  if (!attendee) return null
  const profileShareable = profiles[attendeeId]

  const briefActions: BriefActions = {
    profile: !profileShareable || !attendee.showEditProfileLinkInBrief ? null : { submitted: profileShareable.status === 'Submitted', baseUrl: `${shareUrlsByType.attendee}/${profileShareable.code}` },
    terms: getMediaTermsBriefActions(event.mediaTerms.attendee, attendee),
    sessions: [],
  }

  mutateActionsCounts(briefActions)
  return briefActions
}



export const getResourceBriefActions = (profiles: { [id: string]: PublicEditProfileInfo }, event: JoiEvent, 
  budget: Budget, quoteInfos: { [id: string]: PublicQuoteInfo }, associatedAssetInfos: { [id: string]: PublicEditEntityAssetsInfo }, resourceId: string): BriefActions => {

  if (!event?.resources) return null
  const resource = event.resources[resourceId]

  if (!resource) return null
  const profileShareable = profiles[resourceId]

  const associatedAssetInfo = !associatedAssetInfos ? null : associatedAssetInfos[resourceId]

  const briefActions: BriefActions = {
    associatedAssets: !resource.showAssetsLinkInBrief || !associatedAssetInfo || associatedAssetInfo.status === 'Off' ? null : {
      submitted: associatedAssetInfo.status === 'Submitted',
      baseUrl: `${shareUrlsByType.entityAsset}/${associatedAssetInfo.code}`
    },
    profile: !profileShareable || !resource.showEditProfileLinkInBrief ? null : { submitted: profileShareable.status === 'Submitted', baseUrl: `${shareUrlsByType.resource}/${profileShareable.code}` },
    terms: getMediaTermsBriefActions(event.mediaTerms.resource, resource),
    quotes: resource.quoteIds.map(id => {
      const quoteSection = budget.quotes[id]
      const quoteInfo = quoteInfos[id]
      console.log({ id, quoteSection, quoteInfo })
      if (!quoteInfo || !quoteSection || !quoteInfo.editLinkInBrief || quoteInfo.status === 'Off') return null
      return { 
        name: quoteSection.name,
        submitted: quoteInfo.status === 'Submitted',
        baseUrl: `${shareUrlsByType.quote}/${id}`
      }}).filter(r => r)
  }

  mutateActionsCounts(briefActions)
  return briefActions
}

export const filterEntityMediaTerms = <E extends EntityBaseWithDeprecations>(allTerms: MediaTerms[], entity: E) =>
  allTerms.filter(t => !t.typeIds?.length || t.typeIds.includes(entity.entityTypeId))

export const getEntityMediaTerms = (event: JoiEvent, { type, id }: { type: CoreEntityType, id: string }) => {
  const allEntityTerms: MediaTerms[] = !event || !type ? [] : event.mediaTerms[type]
  const entity = getEntity(event, id, type)
  // console.log({ entity, allEntityTerms, event, id, type })
  if (!entity) return []
  const filteredTerms = filterEntityMediaTerms(allEntityTerms, entity)
  // console.log({ filteredTerms })
  return filteredTerms
}